import React, { useState, useRef, ReactNode } from 'react';
import jsonpath from 'jsonpath';
import {
  TextField,
  StandardTextFieldProps,
  OutlinedTextFieldProps,
} from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete as MuiAutocomplete } from '@material-ui/lab';

import { forLast } from '../../core/utils/performanceUtils';
import styles from './Autocomplete.styles';

type Props<T> = {
  options: T[];
  renderOption?: (option: T) => ReactNode;
  getOptionLabel?: (option: T) => string;
  displayMember?: string;
  valueMember?: string;
  open?: boolean;
  loading?: boolean;
  textFieldProps?: StandardTextFieldProps | OutlinedTextFieldProps;
  value?: T;
  disableClearable?: boolean;
  onQueryChange?: (query: string) => void;
  onBlur?: () => void;
  onChange?: (newValue: T) => void;
  onEnterKeyDown?: (inputValue: string) => void;
};

const useStyles = makeStyles(styles);

function createIsOptionSelectedFunc(valueMember?: string) {
  return (option: unknown, value: unknown) => {
    const optionItem = option as Record<string, unknown>;
    const valueItem = value as Record<string, unknown>;

    if (!valueMember) {
      return optionItem === valueItem;
    }

    if (!optionItem) {
      return false;
    }

    if (!valueItem) {
      return false;
    }

    return optionItem[valueMember] === valueItem[valueMember];
  };
}

function createOptionLabelFunc(displayMember?: string) {
  return (option: unknown) => {
    const optionItem = option as Record<string, unknown>;
    if (!displayMember) {
      return option as string || '';
    }

    if (typeof optionItem === 'object') {
      const text = jsonpath.value(optionItem, `$.${displayMember}`) as string;
      return text || '';
    }

    return optionItem;
  };
}

function hasValueInOptions<T>(options: T[], valueMember?: string, value?: T) {
  if (!value) {
    return false;
  }

  if (!valueMember) {
    return options.includes(value);
  }

  const optionValues = options
    .map((option) => (option as Record<string, unknown>)[valueMember]);

  const valueData = (value as Record<string, unknown>)[valueMember];
  return optionValues.includes(valueData);
}

const Autocomplete = <T extends unknown>({
  options,
  renderOption,
  displayMember,
  valueMember,
  open,
  loading,
  textFieldProps,
  value,
  onQueryChange,
  onBlur,
  getOptionLabel,
  disableClearable,
  onChange,
  onEnterKeyDown,
}: Props<T>) => {
  const [isOpen, setIsOpen] = useState(open);
  const [internalValue, setInternalValue] = useState(value);
  const [preventOpen, setPreventOpen] = useState(false);

  const internalOptions = [...options];
  const lastInputReason = useRef<string>('');

  if (value !== internalValue) {
    setInternalValue(value);
  }

  const handleInternalChange = (
    _: React.ChangeEvent<{}>,
    newValue: unknown,
  ) => {
    setInternalValue(newValue as T);

    if (!newValue && lastInputReason.current === 'input') {
      return;
    }

    if (onChange) {
      onChange(newValue as T);
    }
  };

  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    setPreventOpen(true);
    if (inputValue === '') {
      setIsOpen(false);
      return;
    }

    const finalValue = await forLast(inputValue, 250);
    if (onQueryChange) {
      onQueryChange(finalValue);
      setPreventOpen(false);
    }
  };

  const isValueInOptions = hasValueInOptions<T>(options, valueMember, internalValue);
  if (!isValueInOptions && internalValue) {
    internalOptions.push(internalValue);
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const inputValue = (event.target as HTMLInputElement).value;

      if (!inputValue && onChange) {
        onChange(undefined as T);
        return;
      }

      if (onEnterKeyDown) {
        onEnterKeyDown(inputValue);
      }
    }
  };

  const isReadOnly = textFieldProps?.InputProps?.readOnly;

  const classes = useStyles();
  return (
    <MuiAutocomplete
      classes={{
        root: classes.root,
        noOptions: classes.noOptions,
        loading: classes.loading,
      }}
      open={isOpen && !isReadOnly && !loading && !preventOpen}
      onOpen={() => {
        setIsOpen(true);
      }}
      onClose={() => {
        setIsOpen(false);
      }}
      getOptionSelected={createIsOptionSelectedFunc(valueMember)}
      getOptionLabel={getOptionLabel || createOptionLabelFunc(displayMember)}
      options={internalOptions}
      autoComplete
      disableClearable={isReadOnly || disableClearable}
      loading={loading}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          InputProps={{
            ...!!textFieldProps && textFieldProps.InputProps,
            ...params.InputProps,
          }}
          inputProps={{
            ...!!textFieldProps && textFieldProps.inputProps,
            ...params.inputProps,
            autoComplete: 'nofill', // Chrome specific.
            onKeyDown: handleKeyDown,
          }}
          onChange={handleInputChange}
        />
      )}
      value={internalValue || null}
      onChange={handleInternalChange}
      onFocus={() => setIsOpen(false)}
      onBlur={onBlur}
      onInputChange={(_, val, reason) => {
        if (!val) {
          lastInputReason.current = reason;
        }
      }}
      popupIcon={<SearchIcon fontSize="small" />}
    />
  );
};

export default Autocomplete;
